import { defineStore } from 'pinia'
import { useHttpOnlyAuth, clearAuthToken, getAuthToken } from '~/composables/useHttpOnlyAuth'
import type { Permission } from '~/types/permissions'

interface AdminUser {
  id: number
  name: string
  email: string
  phone?: string
  address?: string
  company_name?: string
  vat_number?: string
  role: string
  role_label: string
  spatie_role?: string
  spatie_roles?: string[]
  is_admin: boolean
  is_super_admin: boolean
  permissions: string[]
  direct_permissions?: string[]
  role_permissions?: string[]
  created_at?: string
  last_login?: string
}

interface AdminStats {
  total_machines: number
  total_brands: number
  total_categories: number
  total_users: number
  recent_machines: number
  recent_inquiries: number
}

interface ActivityItem {
  id: number
  type: string
  title: string
  description: string
  timestamp: string
  user?: string
}

interface AdminNotification {
  id: number
  type: string
  title: string
  message: string
  data: any
  is_read: boolean
  read_at: string | null
  created_at: string
  updated_at: string
}

interface NotificationStats {
  total: number
  unread: number
  read: number
  today: number
  this_week: number
  by_type: Record<string, number>
}

export const useAdminStore = defineStore('admin', {
  state: () => ({
    user: null as AdminUser | null,
    isLoading: false,
    isInitializing: false,
    stats: null as AdminStats | null,
    recentActivity: [] as ActivityItem[],
    notifications: [] as AdminNotification[],
    notificationStats: null as NotificationStats | null,
    unreadCount: 0,
    notificationPollingIntervalId: null as ReturnType<typeof setInterval> | null,
    sidebarCollapsed: false,
    sidebarMobileOpen: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => state.user?.is_admin || false,
    isSuperAdmin: (state) => state.user?.is_super_admin || false,
    userDisplayName: (state) => {
      if (!state.user) return 'Admin'
      return state.user.name || state.user.email || 'Admin'
    },
    /**
     * Check if user has a specific permission
     * Super admins have all permissions (denoted by '*')
     */
    hasPermission: (state) => (permission: string) => {
      if (!state.user) return false
      return state.user.permissions.includes('*') || state.user.permissions.includes(permission)
    },
    /**
     * Check if user has ANY of the specified permissions
     */
    hasAnyPermission: (state) => (permissions: string[]) => {
      if (!state.user) return false
      if (state.user.permissions.includes('*')) return true
      return permissions.some(p => state.user?.permissions.includes(p))
    },
    /**
     * Check if user has ALL of the specified permissions
     */
    hasAllPermissions: (state) => (permissions: string[]) => {
      if (!state.user) return false
      if (state.user.permissions.includes('*')) return true
      return permissions.every(p => state.user?.permissions.includes(p))
    },
    /**
     * Check if user can access a module (has any permission for it)
     */
    canAccessModule: (state) => (module: string) => {
      if (!state.user) return false
      if (state.user.permissions.includes('*')) return true
      return state.user.permissions.some(p => p.startsWith(`${module}.`))
    },
    /**
     * Get user's role (Spatie role name or fallback to enum role)
     */
    userRole: (state) => {
      if (!state.user) return null
      return state.user.spatie_role || state.user.role
    },
    sidebarWidth: (state) => {
      return state.sidebarCollapsed ? '64px' : '224px'
    }
  },

  actions: {
    async login(credentials: { email: string; password: string; remember?: boolean }) {
      this.isLoading = true
      try {
        const { login } = useHttpOnlyAuth()
        const result = await login(credentials)

        if (result.success && result.user) {
          this.user = result.user

          if (process.client && credentials.remember) {
            localStorage.setItem('admin_remember', 'true')
          }

          await navigateTo('/admin')
          return result
        } else {
          throw new Error(result.error || 'Login failed')
        }
      } catch (error: any) {
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async logout() {
      this.isLoading = true
      try {
        const { logout } = useHttpOnlyAuth()
        await logout()

        this.clearAuth()
      } catch (error) {
        console.error('Logout error:', error)
        this.clearAuth()
      } finally {
        this.isLoading = false
      }
    },

    async fetchUser() {
      try {
        const { getCurrentUser } = useHttpOnlyAuth()
        const result = await getCurrentUser()

        if (result.success && result.user) {
          this.user = result.user
          return result
        } else {
          throw new Error(result.error || 'Failed to fetch user')
        }
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    async fetchDashboardData() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/dashboard')

        if ((response as any).data) {
          this.stats = (response as any).data.stats
          this.recentActivity = (response as any).data.recent_activity
        }

        return response
      } catch (error) {
        throw error
      }
    },

    async updateProfile(profileData: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put('/admin/profile', profileData)

        if ((response as any).data) {
          this.user = { ...this.user, ...(response as any).data }
        }

        return response
      } catch (error) {
        throw error
      }
    },

    async updatePassword(passwordData: { current_password: string; password: string; password_confirmation: string }) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put('/admin/password', passwordData)

        return response
      } catch (error) {
        throw error
      }
    },

    async refreshToken() {
      try {
        const { refresh } = useHttpOnlyAuth()
        const result = await refresh()

        if (result.success && result.user) {
          this.user = result.user
          return result
        } else {
          throw new Error(result.error || 'Refresh failed')
        }
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    clearAuth() {
      this.user = null
      this.stats = null
      this.recentActivity = []
      this.isInitializing = false

      // Clear auth token explicitly
      clearAuthToken()

      if (process.client) {
        sessionStorage.clear()
        localStorage.removeItem('admin_remember')
      }

      navigateTo('/admin/login')
    },

    async initializeAuth() {
      if (!process.client) {
        this.isInitializing = false
        return
      }

      if (this.isInitializing) return
      if (this.isAuthenticated) return

      this.isInitializing = true

      try {
        this.initializeSidebarState()

        // Use getAuthToken from useHttpOnlyAuth (same as login uses)
        const storedToken = getAuthToken()

        if (storedToken) {
          await this.fetchUser()
        }
      } catch (error) {
        if (process.dev) {
          console.warn('Auth initialization failed:', error)
        }
        this.clearAuth()
      } finally {
        this.isInitializing = false
      }
    },

    async fetchMachines(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15',
          ...filters
        })

        const response = await $api.get(`/admin/machines?${params}`)
        return {
          data: (response as any).data,
          meta: (response as any).meta
        }
      } catch (error) {
        throw error
      }
    },

    async fetchMachine(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/machines/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createMachine(machineData: any) {
      try {
        const { $api } = useNuxtApp()

        const formData = new FormData()

        Object.keys(machineData).forEach(key => {
          if (key === 'images') {
            if (machineData[key] && Array.isArray(machineData[key])) {
              machineData[key].forEach((file: File, index: number) => {
                formData.append(`images[${index}]`, file)
              })
            }
          } else if (key === 'features') {
            if (machineData[key] && Array.isArray(machineData[key])) {
              machineData[key].forEach((feature: string, index: number) => {
                formData.append(`features[${index}]`, feature)
              })
            }
          } else if (typeof machineData[key] === 'boolean') {
            formData.append(key, machineData[key] ? '1' : '0')
          } else if (machineData[key] !== null && machineData[key] !== '' && machineData[key] !== undefined) {
            if (Array.isArray(machineData[key])) {
              formData.append(key, JSON.stringify(machineData[key]))
            } else {
              formData.append(key, machineData[key])
            }
          }
        })

        const response = await $api.upload('/admin/machines', formData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateMachine(id: string, machineData: any) {
      try {
        const { $api } = useNuxtApp()

        const formData = new FormData()

        Object.keys(machineData).forEach(key => {
          if (key === 'images') {
            if (machineData[key] && Array.isArray(machineData[key])) {
              machineData[key].forEach((file: File, index: number) => {
                formData.append(`images[${index}]`, file)
              })
            }
          } else if (key === 'features') {
            if (machineData[key] && Array.isArray(machineData[key])) {
              machineData[key].forEach((feature: string, index: number) => {
                formData.append(`features[${index}]`, feature)
              })
            }
          } else if (key === 'existing_image_order') {
            if (machineData[key] && Array.isArray(machineData[key])) {
              machineData[key].forEach((imageId: string, index: number) => {
                formData.append(`existing_image_order[${index}]`, imageId)
              })
            }
          } else if (typeof machineData[key] === 'boolean') {
            formData.append(key, machineData[key] ? '1' : '0')
          } else if (machineData[key] !== null && machineData[key] !== '' && machineData[key] !== undefined) {
            if (Array.isArray(machineData[key])) {
              formData.append(key, JSON.stringify(machineData[key]))
            } else {
              formData.append(key, machineData[key])
            }
          }
        })

        formData.append('_method', 'PUT')
        const response = await $api.upload(`/admin/machines/${id}`, formData, 'POST')

        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteMachine(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/machines/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async fetchBrands(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15',
          ...filters
        })

        const response = await $api.get(`/admin/brands?${params}`)
        return {
          data: (response as any).data,
          meta: (response as any).meta
        }
      } catch (error) {
        throw error
      }
    },

    async fetchBrandStats() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/brands/stats')
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchCategories(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15',
          ...filters
        })

        const response = await $api.get(`/admin/categories?${params}`)
        return {
          data: (response as any).data,
          meta: (response as any).meta
        }
      } catch (error) {
        throw error
      }
    },

    async fetchCategory(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/categories/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createCategory(categoryData: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post('/admin/categories', categoryData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateCategory(id: string, categoryData: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put(`/admin/categories/${id}`, categoryData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

          async fetchBrand(id: string) {
            try {
              const { $api } = useNuxtApp()
              const response = await $api.get(`/admin/brands/${id}`)
              return (response as any).data
            } catch (error) {
              throw error
            }
          },

          async createBrand(brandData: FormData) {
            try {
              const { $api } = useNuxtApp()
              const response = await $api.upload('/admin/brands', brandData, 'POST')
              return (response as any).data
            } catch (error) {
              throw error
            }
          },

          async updateBrand(id: string, brandData: FormData) {
            try {
              const { $api } = useNuxtApp()
              brandData.append('_method', 'PUT')
              const response = await $api.upload(`/admin/brands/${id}`, brandData, 'POST') // Use POST for FormData with method spoofing
              return (response as any).data
            } catch (error) {
              throw error
            }
          },

          async deleteBrand(id: string) {
            try {
              const { $api } = useNuxtApp()
              await $api.delete(`/admin/brands/${id}`)
              return true
            } catch (error) {
              throw error
            }
          },

    async deleteCategory(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/categories/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async fetchSpareParts(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15',
          ...filters
        })

        const response = await $api.get(`/admin/spare-parts?${params}`)
        return {
          data: (response as any).data,
          meta: (response as any).meta
        }
      } catch (error) {
        throw error
      }
    },

    async fetchSparePart(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/spare-parts/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createSparePart(sparePartData: any) {
      try {
        const { $api } = useNuxtApp()

        const formData = new FormData()

        Object.keys(sparePartData).forEach(key => {
          if (key === 'image') {
            if (sparePartData[key] && sparePartData[key] instanceof File) {
              formData.append('image', sparePartData[key])
            }
          } else if (sparePartData[key] !== null && sparePartData[key] !== '' && sparePartData[key] !== undefined) {
            formData.append(key, sparePartData[key])
          }
        })

        const response = await $api.upload('/admin/spare-parts', formData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateSparePart(id: string, sparePartData: any) {
      try {
        const { $api } = useNuxtApp()

        const formData = new FormData()

        Object.keys(sparePartData).forEach(key => {
          if (key === 'image') {
            if (sparePartData[key] && sparePartData[key] instanceof File) {
              formData.append('image', sparePartData[key])
            }
          } else if (sparePartData[key] !== null && sparePartData[key] !== '' && sparePartData[key] !== undefined) {
            formData.append(key, sparePartData[key])
          }
        })

        formData.append('_method', 'PUT')
        const response = await $api.upload(`/admin/spare-parts/${id}`, formData, 'POST')

        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteSparePart(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/spare-parts/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async fetchSparePartStats() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/spare-parts-stats')
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchEmailCampaigns(params: any = {}) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/email-campaigns', { params })
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchEmailCampaign(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/email-campaigns/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createEmailCampaign(data: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post('/admin/email-campaigns', data)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateEmailCampaign(id: string, data: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put(`/admin/email-campaigns/${id}`, data)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteEmailCampaign(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/email-campaigns/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async sendEmailCampaign(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post(`/admin/email-campaigns/${id}/send`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async duplicateEmailCampaign(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post(`/admin/email-campaigns/${id}/duplicate`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async getEmailCampaignStats(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/email-campaigns/${id}/stats`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchContacts(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15'
        })

        if (filters.type) params.append('type', filters.type)
        if (filters.status) params.append('status', filters.status)
        if (filters.search) params.append('search', filters.search)

        const response = await $api.get(`/admin/contacts?${params}`)
        return response as any
      } catch (error) {
        throw error
      }
    },

    async getContactStats() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/contacts/stats')
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async markContactResponded(type: string, id: string, notes: string = '') {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put(`/admin/contacts/${type}/${id}/respond`, { notes })
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteContact(type: string, id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/contacts/${type}/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async fetchNotifications(page: number = 1, unreadOnly: boolean = false) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '20'
        })
        if (unreadOnly) params.append('unread_only', 'true')

        const response = await $api.get(`/admin/notifications?${params}`)

        const data = (response as any).data?.data || (response as any).data || []
        this.notifications = Array.isArray(data) ? data : []

        return (response as any).data
      } catch (error) {
        this.notifications = []
        throw error
      }
    },

    async getNotificationStats() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/notifications/stats')

        const data = (response as any).data?.data || (response as any).data || {}

        this.notificationStats = data
        this.unreadCount = data.unread || 0

        return data
      } catch (error) {
        this.notificationStats = {
          total: 0,
          unread: 0,
          read: 0,
          today: 0,
          this_week: 0,
          by_type: {}
        }
        this.unreadCount = 0
        throw error
      }
    },

    async markNotificationAsRead(id: number) {
      try {
        const { $api } = useNuxtApp()
        await $api.put(`/admin/notifications/${id}/read`)

        const notification = this.notifications.find(n => n.id === id)
        if (notification) {
          notification.is_read = true
          notification.read_at = new Date().toISOString()
        }

        if (this.unreadCount > 0) {
          this.unreadCount--
        }

        await this.getNotificationStats()

        return true
      } catch (error) {
        throw error
      }
    },

    async markNotificationAsUnread(id: number) {
      try {
        const { $api } = useNuxtApp()
        await $api.put(`/admin/notifications/${id}/unread`)

        const notification = this.notifications.find(n => n.id === id)
        if (notification) {
          notification.is_read = false
          notification.read_at = null
        }

        this.unreadCount++
        await this.getNotificationStats()

        return true
      } catch (error) {
        throw error
      }
    },

    async markAllNotificationsAsRead() {
      try {
        const { $api } = useNuxtApp()
        await $api.put('/admin/notifications/read-all')

        this.notifications.forEach(notification => {
          notification.is_read = true
          notification.read_at = new Date().toISOString()
        })

        this.unreadCount = 0
        await this.getNotificationStats()

        return true
      } catch (error) {
        throw error
      }
    },

    async deleteNotification(id: number) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/notifications/${id}`)

        const deletedNotification = this.notifications.find(n => n.id === id)
        if (deletedNotification && !deletedNotification.is_read && this.unreadCount > 0) {
          this.unreadCount--
        }

        const index = this.notifications.findIndex(n => n.id === id)
        if (index !== -1) {
          this.notifications.splice(index, 1)
        }

        await this.getNotificationStats()

        return true
      } catch (error) {
        throw error
      }
    },

    async deleteAllReadNotifications() {
      try {
        const { $api } = useNuxtApp()
        await $api.delete('/admin/notifications/read-all')

        this.notifications = this.notifications.filter(n => !n.is_read)
        await this.getNotificationStats()

        return true
      } catch (error) {
        throw error
      }
    },

    startNotificationPolling() {
      if (process.client) {
        if (this.notificationPollingIntervalId) {
          return
        }

        this.notificationPollingIntervalId = setInterval(async () => {
          try {
            await this.getNotificationStats()
          } catch (error) {
          }
        }, 30000)
      }
    },

    stopNotificationPolling() {
      if (this.notificationPollingIntervalId) {
        clearInterval(this.notificationPollingIntervalId)
        this.notificationPollingIntervalId = null
      }
    },

    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      if (process.client) {
        try {
          localStorage.setItem('admin_sidebar_collapsed', this.sidebarCollapsed.toString())
        } catch (error) {
          if (process.dev) {
            console.warn('Failed to save sidebar state:', error)
          }
        }
      }
    },

    toggleMobileSidebar() {
      this.sidebarMobileOpen = !this.sidebarMobileOpen
    },

    closeMobileSidebar() {
      this.sidebarMobileOpen = false
    },

    initializeSidebarState() {
      if (process.client) {
        try {
          const saved = localStorage.getItem('admin_sidebar_collapsed')
          if (saved !== null) {
            this.sidebarCollapsed = saved === 'true'
          }
        } catch (error) {
          if (process.dev) {
            console.warn('Failed to initialize sidebar state:', error)
          }
          this.sidebarCollapsed = false
        }
      } else {
        this.sidebarCollapsed = false
      }
    },

    async fetchAdmins(filters: any = {}, page: number = 1) {
      try {
        const { $api } = useNuxtApp()
        const params = new URLSearchParams({
          page: page.toString(),
          per_page: '15',
          ...filters
        })

        const response = await $api.get(`/admin/admins?${params}`)
        return {
          data: (response as any).data,
          meta: (response as any).meta
        }
      } catch (error) {
        throw error
      }
    },

    async fetchAdminStats() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/admins/stats')
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchAdmin(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/admins/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createAdmin(adminData: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post('/admin/admins', adminData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateAdmin(id: string, adminData: any) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.put(`/admin/admins/${id}`, adminData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteAdmin(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/admins/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async toggleAdminStatus(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.patch(`/admin/admins/${id}/toggle-status`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async resetAdminPassword(id: string, passwordData: { password: string; password_confirmation: string }) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post(`/admin/admins/${id}/reset-password`, passwordData)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async fetchCompanyServices() {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get('/admin/services')
        const data = (response as any).data
        return Array.isArray(data) ? data : []
      } catch (error) {
        console.error('fetchCompanyServices error:', error)
        throw error
      }
    },

    async fetchCompanyService(id: string) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.get(`/admin/services/${id}`)
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async createCompanyService(serviceData: any) {
      try {
        const { $api } = useNuxtApp()
        const formData = new FormData()
        
        Object.keys(serviceData).forEach(key => {
          if (key === 'image' && serviceData[key] instanceof File) {
            formData.append('image', serviceData[key])
          } else if (key === 'extra_images' && Array.isArray(serviceData[key])) {
            serviceData[key].forEach((file: File, index: number) => {
              if (file instanceof File) {
                formData.append(`extra_images[${index}]`, file)
              }
            })
          } else if (typeof serviceData[key] === 'boolean') {
            formData.append(key, serviceData[key] ? '1' : '0')
          } else if (serviceData[key] !== null && serviceData[key] !== undefined) {
            formData.append(key, serviceData[key])
          }

        })

        const response = await $api.upload('/admin/services', formData)

        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async updateCompanyService(id: string, serviceData: any) {
      try {
        const { $api } = useNuxtApp()
        const formData = new FormData()
        
        Object.keys(serviceData).forEach(key => {
          if (key === 'image' && serviceData[key] instanceof File) {
            formData.append('image', serviceData[key])
          } else if (key === 'extra_images' && Array.isArray(serviceData[key])) {
            serviceData[key].forEach((file: File, index: number) => {
              if (file instanceof File) {
                formData.append(`extra_images[${index}]`, file)
              }
            })
          } else if (typeof serviceData[key] === 'boolean') {
            formData.append(key, serviceData[key] ? '1' : '0')
          } else if (serviceData[key] !== null && serviceData[key] !== undefined) {
            formData.append(key, serviceData[key])
          }

        })
        formData.append('_method', 'PUT')

        const response = await $api.upload(`/admin/services/${id}`, formData, 'POST')
        return (response as any).data
      } catch (error) {
        throw error
      }
    },

    async deleteCompanyService(id: string) {
      try {
        const { $api } = useNuxtApp()
        await $api.delete(`/admin/services/${id}`)
        return true
      } catch (error) {
        throw error
      }
    },

    async reorderCompanyServices(orders: { id: number, order: number }[]) {
      try {
        const { $api } = useNuxtApp()
        const response = await $api.post('/admin/services/reorder', { orders })
        return response
      } catch (error) {
        throw error
      }
    }
  }
})
